import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Stack,
  Group,
  Text,
  Badge,
  Table,
  LoadingOverlay,
  Divider
} from '@mantine/core';

import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import Card from 'components/Card';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';

import { rangeTemplateService, RangeTemplate } from 'services/lia/rangeTemplateService';

const items = [
  { title: 'Range Templates', href: '/app/lia/questions/range-templates' },
  { title: 'View Template', href: '' }
];

const ViewRangeTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');
  const [template, setTemplate] = useState<RangeTemplate | null>(null);

  // Fetch template data
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['rangeTemplate', templateId],
    queryFn: () => (templateId ? rangeTemplateService.getDetail(templateId) : null),
    enabled: !!templateId,
    retry: 1
  });

  // Store template data in state once it's loaded
  useEffect(() => {
    if (templateData) {
      if (templateData.data && typeof templateData.data === 'object') {
        setTemplate(templateData.data);
      } else {
        setTemplate(templateData as unknown as RangeTemplate);
      }
    }
  }, [templateData]);

  if (!templateId) {
    navigate('/app/lia/questions/range-templates');
    return null;
  }

  return (
    <Container fluid>
      <Stack gap='md'>
        <BreadcrumbsComponent items={items} />

        <Card>
          <div style={{ position: 'relative' }}>
            <LoadingOverlay visible={isLoading || !template} />

            {template && (
              <Stack gap='md'>
                <Group align='center' justify='space-between'>
                  <Group>
                    <Text fw={700} size='lg'>
                      {template.templateName || 'Unnamed Template'}
                    </Text>
                    {template.isDefault && <Badge color='green'>Default Template</Badge>}
                  </Group>
                  <Text size='sm' c='dimmed'>
                    Created:{' '}
                    {template.dateIn ? new Date(template.dateIn).toLocaleDateString() : 'N/A'}
                  </Text>
                </Group>

                {template.description && <Text color='dimmed'>{template.description}</Text>}

                <Divider my='sm' />

                <Text fw={700}>Range Items</Text>

                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th>Sequence</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {!template.items || template.items.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                          No range items in this template
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      template.items.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text>{item.label}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text>{item.value}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text>{item.sequence}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>

                <Group justify='flex-end' mt='xl'>
                  <SecondaryButton
                    text='Back'
                    onClick={() => navigate('/app/lia/questions/range-templates')}
                    mr={10}
                  />
                  <PrimaryButton
                    text='Edit Template'
                    onClick={() =>
                      navigate(`/app/lia/questions/range-templates/edit?id=${templateId}`)
                    }
                  />
                </Group>
              </Stack>
            )}
          </div>
        </Card>
      </Stack>
    </Container>
  );
};

export default ViewRangeTemplatePage;
